import type { FaqBlockPayload } from '@opinix-cloud/shared';

interface PricingFAQProps {
  /** When omitted, renders the bundled defaults so the section never
   *  appears blank during initial mount. */
  content?: FaqBlockPayload;
}

const DEFAULTS: FaqBlockPayload = {
  eyebrow: 'FAQ',
  heading: 'Common questions.',
  items: [
    {
      q: 'Can I switch plans?',
      a: 'Yes. Upgrade or downgrade any time from your tenant settings. Changes take effect at the start of your next billing period; usage limits adjust immediately on upgrade.',
    },
    {
      q: 'How does billing work?',
      a: 'Self-serve plans (Free, Starter, Pro) are billed monthly or annually by credit card. Annual billing saves you two months. Enterprise plans are quoted with custom terms and can be billed annually by invoice or PO.',
    },
    {
      q: "What's an appliance?",
      a: 'An Opinix appliance is the on-prem (or cloud-hosted) edge device that runs the simulation comms layer for an exercise. Each physical training site or simultaneous large-scale drill typically maps to one appliance — three appliances on Starter is enough for most three-site agencies.',
    },
    {
      q: 'What about air-gapped deployments?',
      a: 'Available on Enterprise. We deliver Opinix as a self-contained image you run inside your own network — no inbound or outbound connectivity to our cloud required. Updates and scenario packs ship as signed bundles you side-load on your schedule.',
    },
  ],
};

export function PricingFAQ({ content }: PricingFAQProps): JSX.Element {
  const data = content ?? DEFAULTS;
  return (
    <section className="bg-steel-50 py-24 sm:py-32 border-t border-steel-200">
      <div className="container-x">
        <div className="max-w-2xl">
          {data.eyebrow && <p className="eyebrow">{data.eyebrow}</p>}
          {data.heading && (
            <h2 className="mt-3 section-title">{data.heading}</h2>
          )}
        </div>

        <dl className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {data.items.map((item) => (
            <div key={item.q}>
              <dt className="text-base font-bold text-navy-900 tracking-tight">
                {item.q}
              </dt>
              <dd className="mt-3 text-sm text-steel-600 leading-relaxed">
                {item.a}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
